/**
 * Category → DB column for segment tech-stack rules.
 * Vendor pickers are derived from accounts at runtime (see buildTechStackCategories).
 */
export const TECH_STACK_FIELDS = {
  LMS: 'lms_vendor',
  SIS: 'sis_vendor',
  CRM: 'crm_vendor',
  Assessment: 'assessment_vendor',
  Evaluation: 'evaluation_vendor',
  Library: 'library_vendor',
  Tutor: 'tutor_vendor',
  Support: 'support_vendor',
  Research: 'research_vendor',
  Authoring: 'authoring_vendor',
  Immersive: 'immersive_vendor',
  EHR: 'ehr_vendor',
  iPass: 'ipass_vendor',
  'Patient Portal': 'patientportal_vendor',
  Admissions: 'admissions_vendor',
  'School-to-Home': 'home_vendor',
  Tower: 'tower_vendor',
  Proctoring: 'proctoring_vendor',
  ePortfolio: 'eportfolio_vendor',
};

/** Sentinel for "no vendor" matching; appended to each category vendor list in buildTechStackCategories */
export const TECH_VENDOR_UNKNOWN = 'No vendor / unknown';

/**
 * @param {Array<Record<string, unknown>>} allAccounts
 * @returns {Record<string, { field: string, vendors: string[] }>}
 */
export function buildTechStackCategories(allAccounts) {
  const out = {};
  for (const [label, field] of Object.entries(TECH_STACK_FIELDS)) {
    const vendors = Array.from(
      new Set(
        allAccounts
          .map((a) => a?.[field])
          .filter((v) => v && String(v).trim())
      )
    ).sort((a, b) => String(a).localeCompare(String(b)));
    if (vendors.length > 0) {
      out[label] = { field, vendors: [...vendors, TECH_VENDOR_UNKNOWN] };
    }
  }
  return out;
}

/**
 * @param {Array<Record<string, unknown>>} accounts
 * @param {Array<Record<string, unknown>>} rules
 * @param {Record<string, { field: string, vendors: string[] }>} techStackCategories
 */
export function applyRules(accounts, rules, techStackCategories) {
  const stackMap = techStackCategories && typeof techStackCategories === 'object' ? techStackCategories : {};
  const vendorFieldList = Object.values(TECH_STACK_FIELDS);

  if (!rules || rules.length === 0) return accounts;

  console.log('🔧 segmentApplyRules called with:', {
    accountCount: accounts.length,
    ruleCount: rules.length,
    rules: rules.map((r) => ({
      field: r.field,
      operator: r.operator,
      value: r.value,
      category: r.category,
    })),
  });

  const matched = accounts.filter((account) => {
    const matchesAllRules = rules.every((rule) => {
      if (rule.operator === 'in') {
        let ruleVal = rule.value;
        if (typeof ruleVal === 'string' && ruleVal.startsWith('[')) {
          try {
            ruleVal = JSON.parse(ruleVal);
          } catch {
            ruleVal = [];
          }
        }
        if (!Array.isArray(ruleVal) || ruleVal.length === 0) {
          console.log('⏭️ segmentApplyRules: Skipping empty rule for field:', rule.field);
          return true;
        }
      }

      let accountValue;

      if (rule.field === 'tech_stack') {
        if (rule.category) {
          const categoryConfig = stackMap[rule.category];
          if (categoryConfig) {
            accountValue = account[categoryConfig.field];
          } else {
            accountValue = [];
          }
        } else {
          const techStackVendors = (account.tech_stack || []).map((tech) => tech.vendor);
          const productFields = vendorFieldList.map((field) => account[field]).filter(Boolean);
          accountValue = [...techStackVendors, ...productFields];
        }
      } else {
        accountValue = account[rule.field];
      }

      let ruleValue = rule.value;
      if (!Array.isArray(ruleValue) && typeof ruleValue === 'string' && ruleValue.startsWith('[')) {
        try {
          ruleValue = JSON.parse(ruleValue);
        } catch {
          ruleValue = rule.value;
        }
      }

      if (rule.field === 'decision_window_band') {
        const bandMap = {
          '<12m': '<12 months',
          '<12 months': '<12 months',
          '12-24m': '12-24 months',
          '12-24 months': '12-24 months',
          '24+m': '>24 months',
          '>24m': '>24 months',
          '>24 months': '>24 months',
          'N/A': 'N/A',
        };
        const mappedAccountValue =
          accountValue == null ? 'N/A' : bandMap[accountValue] || accountValue;
        const mapRuleBand = (v) => (v == null ? 'N/A' : bandMap[v] || v);
        if (rule.operator === '==' && mappedAccountValue === mapRuleBand(ruleValue)) return true;
        if (rule.operator === 'in' && Array.isArray(ruleValue)) {
          const normalizedRuleValues = ruleValue.map((rv) => mapRuleBand(rv));
          if (normalizedRuleValues.includes(mappedAccountValue)) return true;
        }
        return false;
      }

      if (rule.field === 'tech_stack' && rule.category && rule.operator === 'in') {
        const categoryConfig = stackMap[rule.category];
        const accountVendor = categoryConfig ? account[categoryConfig.field] : undefined;
        if (!Array.isArray(ruleValue) || ruleValue.length === 0) {
          return true;
        }
        const matchesToken = (rv) => {
          if (String(rv) === TECH_VENDOR_UNKNOWN) {
            return accountVendor == null || String(accountVendor).trim() === '';
          }
          if (accountVendor == null || String(accountVendor).trim() === '') return false;
          const avLower = String(accountVendor).toLowerCase();
          const rvLower = String(rv).toLowerCase();
          return avLower.includes(rvLower) || rvLower.includes(avLower);
        };
        return ruleValue.some(matchesToken);
      }

      let comparableAccountValue = accountValue;
      if (
        rule.operator === 'in' &&
        !Array.isArray(accountValue) &&
        accountValue !== null &&
        accountValue !== undefined
      ) {
        comparableAccountValue = [String(accountValue)];
      }

      if (accountValue === undefined || accountValue === null) {
        console.log('❌ segmentApplyRules: accountValue undefined/null for field:', rule.field, 'account:', account.account_name);
        return false;
      }

      let result = false;
      switch (rule.operator) {
        case '>=':
          result = parseFloat(accountValue) >= parseFloat(ruleValue);
          break;
        case '<=':
          result = parseFloat(accountValue) <= parseFloat(ruleValue);
          break;
        case '==':
          if (
            rule.field === 'procurement_friendliness' ||
            rule.field === 'incumbent_sentiment'
          ) {
            result = String(accountValue).toLowerCase() === String(ruleValue).toLowerCase();
          } else {
            result = String(accountValue) === String(ruleValue);
          }
          break;
        case 'in':
          // Normalize casing for case-sensitive enum fields stored as free text.
          if (rule.field === 'incumbent_sentiment') {
            const av = String(accountValue || '').toLowerCase();
            const rvLower = (Array.isArray(ruleValue) ? ruleValue : [ruleValue]).map((v) =>
              String(v).toLowerCase()
            );
            result = rvLower.includes(av);
            break;
          }
          if (Array.isArray(comparableAccountValue)) {
            if (rule.field === 'tech_stack') {
              result =
                Array.isArray(ruleValue) &&
                ruleValue.some((rv) => {
                  const rvLower = String(rv).toLowerCase();
                  return comparableAccountValue.some((av) => {
                    const avLower = String(av).toLowerCase();
                    return avLower.includes(rvLower) || rvLower.includes(avLower);
                  });
                });
            } else {
              result = Array.isArray(ruleValue) && ruleValue.some((rv) => comparableAccountValue.includes(rv));
            }
          } else if (rule.field === 'tech_stack') {
            const avLower = String(accountValue).toLowerCase();
            result =
              Array.isArray(ruleValue) &&
              ruleValue.some((rv) => {
                const rvLower = String(rv).toLowerCase();
                return avLower.includes(rvLower) || rvLower.includes(avLower);
              });
          } else {
            result = Array.isArray(ruleValue) && ruleValue.includes(String(accountValue));
          }
          break;
        default:
          result = false;
      }

      if (!result) {
        console.log('❌ segmentApplyRules rule failed:', {
          account: account.account_name,
          field: rule.field,
          operator: rule.operator,
          accountValue,
          ruleValue,
          result,
        });
      }

      return result;
    });

    return matchesAllRules;
  });

  console.log('✅ segmentApplyRules result:', {
    inputAccounts: accounts.length,
    matchedAccounts: matched.length,
    matchedIds: matched.map((a) => a.id),
    matchedNames: matched.map((a) => a.account_name),
  });

  return matched;
}
